import * as React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import useBreadcrumbs from 'use-react-router-breadcrumbs';
import _ from 'lodash';
import { PermWrapper } from '@/components/permwrapper';
import { ERoles } from '@/auth/constants';
import {
	Box,
	Divider,
	List,
	ListItemButton,
	ListItemIcon,
	ListItemText
} from '@mui/material';
import {
	Campaign,
	DesignServices,
	FileCopy,
	KeyboardArrowDown,
	LocationCity,
	Phone,
	Schedule,
	ScheduleSend,
	Settings,
	Sync,
	SvgIconComponent
} from '@mui/icons-material';
import logo from '@/assets/images/pcomm_offset.svg';

export const NavigationList = () => {
	return (
		<List
			sx={{
				width: 250,
				paddingTop: 0,
				display: 'flex',
				flexGrow: 1,
				flexDirection: 'column'
			}}>
			<>
				<HomeNav />
				<Divider sx={{ bgcolor: '#91919161' }} />
			</>
			<PermWrapper allowedRoles={[ERoles.ADMIN]}>
				<>
					<SyncNav />
					<Divider sx={{ bgcolor: '#91919161' }} />
				</>
			</PermWrapper>
			<PermWrapper
				allowedRoles={[
					ERoles.ADMIN,
					ERoles.TEMPLATE_ADMIN,
					ERoles.TEMPLATE_MANAGER
				]}>
				<>
					<TemplatesNav />
					<Divider sx={{ bgcolor: '#91919161' }} />
				</>
			</PermWrapper>
			<PermWrapper
				allowedRoles={[
					ERoles.ADMIN,
					ERoles.SCHEDULE_MANAGER,
					ERoles.SCHEDULE_READONLY
				]}>
				<>
					<ScheduleNav />
					<Divider sx={{ bgcolor: '#91919161' }} />
				</>
			</PermWrapper>
			<PermWrapper
				allowedRoles={[
					ERoles.ADMIN,
					ERoles.SETTINGS_MANAGER,
					ERoles.SETTINGS_READONLY
				]}>
				<>
					<SettingsNav />
					<Divider sx={{ bgcolor: '#91919161' }} />
				</>
			</PermWrapper>
		</List>
	);
};

const CollapsibleNavButton = (props: {
	open: boolean;
	setOpen: (open: boolean) => void;
	title: string;
	Icon: SvgIconComponent;
}) => {
	const { open, setOpen, title, Icon } = props;
	return (
		<ListItemButton
			alignItems='flex-start'
			onClick={() => setOpen(!open)}
			sx={{
				px: 2,
				pt: 1.5,
				pb: 1.5
			}}>
			<ListItemIcon sx={{ color: 'inherit', minWidth: 35, margin: 0 }}>
				<Icon fontSize='small' />
			</ListItemIcon>
			<ListItemText
				primary={title}
				primaryTypographyProps={{
					fontSize: 14,
					fontWeight: '600',
					lineHeight: '20px',
					mb: '2px',
					variant: 'button'
				}}
				sx={{ my: 0 }}
			/>
			<KeyboardArrowDown
				sx={{
					mr: -1,
					transform: open ? 'rotateX(-180deg)' : 'rotateX(0)',
					transition: '0.2s'
				}}
			/>
		</ListItemButton>
	);
};

const NavigationListItemLink = (props: {
	to: string;
	label: string;
	Icon: SvgIconComponent;
}) => {
	const { to, label, Icon } = props;
	const location = useLocation();
	const breadcrumbs = useBreadcrumbs();
	const exists = _.some(breadcrumbs, bc => bc.key === to);
	const selected = location.pathname === to || exists;

	return (
		<ListItemButton
			selected={selected}
			key={label}
			component={RouterLink}
			to={to}
			sx={{
				pt: 0.5,
				pb: 0.5,
				'&.Mui-selected': {
					color: theme => theme.palette.common.white,
					backgroundColor: theme => theme.palette.primary.dark,
					'&:hover': {
						color: theme => theme.palette.common.white,
						backgroundColor: theme => theme.palette.info.main
					}
				},
				'&:hover': {
					color: theme => theme.palette.common.white,
					backgroundColor: theme => theme.palette.info.main
				}
			}}>
			<ListItemIcon sx={{ color: 'inherit', minWidth: 35, margin: 0 }}>
				<Icon fontSize='small' />
			</ListItemIcon>
			<ListItemText
				primary={label}
				primaryTypographyProps={{
					fontSize: 14,
					fontWeight: '500'
				}}
			/>
		</ListItemButton>
	);
};

const HomeNav = () => {
	const location = useLocation();
	const selected = location.pathname === '/';
	return (
		<ListItemButton
			to='/'
			selected={selected}
			key='home'
			component={RouterLink}
			sx={{
				flex: 0,
				justifyContent: 'center',
				'&.Mui-selected': {
					'&:hover': {
						color: theme => theme.palette.common.white,
						backgroundColor: theme => theme.palette.info.main
					}
				},
				'&:hover': {
					color: theme => theme.palette.common.white,
					backgroundColor: theme => theme.palette.info.main
				}
			}}>
			<img
				width={75}
				height='auto'
				src={logo}
				alt='Precision Comm'
			/>
		</ListItemButton>
	);
};

const SyncNav = () => {
	const { pathname } = useLocation();
	const [open, setOpen] = React.useState(
		_.includes(pathname, '/sync/proxyphones')
	);
	return (
		<Box>
			<CollapsibleNavButton
				title='Sync'
				open={open}
				setOpen={setOpen}
				Icon={Sync}
			/>
			{open && (
				<>
					<NavigationListItemLink
						label='Proxy Phones'
						to='/sync/proxyphones'
						Icon={Phone}
					/>
				</>
			)}
		</Box>
	);
};

const TemplatesNav = () => {
	const { pathname } = useLocation();
	const [open, setOpen] = React.useState(_.includes(pathname, '/templates'));
	return (
		<Box>
			<CollapsibleNavButton
				title='Templates'
				open={open}
				setOpen={setOpen}
				Icon={DesignServices}
			/>
			{open && (
				<>
					<NavigationListItemLink
						label='Email Replication'
						to='/templates/email/replication'
						Icon={FileCopy}
					/>
				</>
			)}
		</Box>
	);
};

const ScheduleNav = () => {
	const { pathname } = useLocation();
	const [open, setOpen] = React.useState(_.includes(pathname, '/schedule'));
	return (
		<Box>
			<CollapsibleNavButton
				title='Schedule'
				open={open}
				setOpen={setOpen}
				Icon={Schedule}
			/>
			{open && (
				<>
					<NavigationListItemLink
						label='Actions'
						to='/schedule/actions'
						Icon={ScheduleSend}
					/>
				</>
			)}
		</Box>
	);
};

const SettingsNav = () => {
	const { pathname } = useLocation();
	const [open, setOpen] = React.useState(_.includes(pathname, '/settings'));
	return (
		<Box>
			<CollapsibleNavButton
				title='Settings'
				open={open}
				setOpen={setOpen}
				Icon={Settings}
			/>
			{open && (
				<>
					<NavigationListItemLink
						label='Campaigns'
						to='/settings/campaigns'
						Icon={Campaign}
					/>
					<NavigationListItemLink
						label='Campaigns By Region'
						to='/settings/regioncampaigns'
						Icon={LocationCity}
					/>
				</>
			)}
		</Box>
	);
};
